import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, FileText, Award, Route, Search, X } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const TYPE_CONFIG = {
  course:      { icon: BookOpen,  label: 'Formations',   color: '#20c997' },
  module:      { icon: FileText,  label: 'Modules',      color: '#ffd93d' },
  path:        { icon: Route,     label: 'Parcours',     color: '#ff6b6b' },
  referential: { icon: Award,     label: 'Référentiels', color: '#a78bfa' },
};

export default function CmdK({ onClose }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const search = useCallback(async (q) => {
    if (!q || q.length < 2) { setResults([]); return; }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/search?q=${encodeURIComponent(q)}`, { withCredentials: true });
      setResults(res.data.results || []);
      setActiveIndex(0);
    } catch (_) {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(val), 200);
  };

  const handleSelect = (result) => {
    navigate(result.url);
    onClose();
  };

  const handleKey = (e) => {
    if (e.key === 'Escape') { onClose(); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIndex(i => Math.min(i + 1, results.length - 1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIndex(i => Math.max(i - 1, 0)); }
    if (e.key === 'Enter' && results[activeIndex]) { handleSelect(results[activeIndex]); }
  };

  // Group results by type
  const grouped = results.reduce((acc, r) => {
    if (!acc[r.type]) acc[r.type] = [];
    acc[r.type].push(r);
    return acc;
  }, {});

  return (
    <div
      className="fixed inset-0 z-[200] flex items-start justify-center pt-[10vh]"
      style={{ background: 'rgba(15,14,23,0.8)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}
      data-testid="cmdk-overlay"
    >
      <div
        className="w-full max-w-xl mx-4 bg-[#16161e] border border-[#2a2935] rounded-2xl shadow-2xl overflow-hidden"
        onClick={e => e.stopPropagation()}
        data-testid="cmdk-dialog"
      >
        {/* Search input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-[#2a2935]">
          <Search size={16} className="text-[#64748b] shrink-0" />
          <input
            ref={inputRef}
            value={query}
            onChange={handleChange}
            onKeyDown={handleKey}
            placeholder="Rechercher formations, modules, parcours..."
            className="flex-1 bg-transparent text-white text-sm outline-none placeholder:text-[#64748b]"
            data-testid="cmdk-input"
          />
          {loading && (
            <div className="w-3.5 h-3.5 border-2 border-[#20c997] border-t-transparent rounded-full animate-spin shrink-0" />
          )}
          <button onClick={onClose} className="text-[#64748b] hover:text-white transition-colors">
            <X size={14} />
          </button>
        </div>

        {/* Results */}
        <div className="max-h-[55vh] overflow-y-auto" data-testid="cmdk-results">
          {query.length >= 2 && results.length === 0 && !loading && (
            <p className="px-4 py-6 text-sm text-[#64748b] text-center">Aucun résultat pour « {query} »</p>
          )}
          {query.length < 2 && (
            <p className="px-4 py-6 text-xs text-[#64748b] text-center">Tapez au moins 2 caractères...</p>
          )}
          {Object.entries(grouped).map(([type, items]) => {
            const cfg = TYPE_CONFIG[type] || { icon: Search, label: type, color: '#94a3b8' };
            const Icon = cfg.icon;
            return (
              <div key={type} className="py-2">
                <p className="px-4 pb-1 text-[10px] uppercase tracking-[0.2em] font-bold" style={{ color: cfg.color }}>
                  {cfg.label}
                </p>
                {items.map((result, i) => {
                  const globalIdx = results.indexOf(result);
                  const isActive = globalIdx === activeIndex;
                  return (
                    <button
                      key={result.id}
                      onClick={() => handleSelect(result)}
                      onMouseEnter={() => setActiveIndex(globalIdx)}
                      className={`w-full flex items-start gap-3 px-4 py-2.5 text-left transition-colors ${isActive ? 'bg-[#20202a]' : 'hover:bg-[#20202a]'}`}
                      data-testid={`cmdk-result-${result.id}`}
                    >
                      <Icon size={15} style={{ color: cfg.color }} className="mt-0.5 shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm text-white truncate">{result.title}</p>
                        {result.subtitle && (
                          <p className="text-xs text-[#64748b] truncate">{result.subtitle}</p>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="border-t border-[#2a2935] px-4 py-2 flex items-center gap-4 text-[10px] text-[#64748b]">
          <span><kbd className="bg-[#2a2935] px-1.5 py-0.5 rounded text-[10px] mr-1">↑↓</kbd>naviguer</span>
          <span><kbd className="bg-[#2a2935] px-1.5 py-0.5 rounded text-[10px] mr-1">↵</kbd>ouvrir</span>
          <span><kbd className="bg-[#2a2935] px-1.5 py-0.5 rounded text-[10px] mr-1">Esc</kbd>fermer</span>
        </div>
      </div>
    </div>
  );
}
